import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { logError } from '@/utils/logHandling';
import ConfirmModal from '@/components/SharedComponents/ConfirmModal/ConfirmModal';
import { constants } from '@/config/constants';
import { DemoModal } from '@/components/Home/DemoModal';
import { useGlobalStateInHome } from '@/hooks/useGlobalStateInHome';
import { useOverlayGuard } from '@/hooks/useOverlayGuard';

interface PatientDemoContextType {
  showDemoPopUp: boolean;
  setShowDemoPopUp: React.Dispatch<React.SetStateAction<boolean>>;
  logoutFromDemo: boolean;
  popupTimerRef: React.RefObject<number | null>;
  popupDelayMs: number;
  setLogoutFromDemo: React.Dispatch<React.SetStateAction<boolean>>;
  setShowWelcomeModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const PatientDemoContext = createContext<PatientDemoContextType | undefined>(
  undefined,
);

interface PatientDemoProviderProps {
  children: ReactNode;
  popupDelayMs?: number;
}

export function PatientDemoProvider({
  children,
  popupDelayMs = constants.popupDelayMs,
}: PatientDemoProviderProps) {
  const { isPatientDemo } = useGlobalStateInHome();
  const [logoutFromDemo, setLogoutFromDemo] = useState(false);
  const [showDemoPopUp, setShowDemoPopUp] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  const popupTimerRef = useRef<number | null>(null);

  useOverlayGuard(showWelcomeModal);

  useEffect(() => {
    if (!isPatientDemo) {
      return;
    }
    popupTimerRef.current = setTimeout(() => {
      setShowDemoPopUp(true);
    }, popupDelayMs);

    return () => {
      if (popupTimerRef.current !== null) {
        clearTimeout(popupTimerRef.current);
        popupTimerRef.current = null;
      }
    };
  }, [isPatientDemo]);

  const value = {
    showDemoPopUp,
    setShowDemoPopUp,
    logoutFromDemo,
    popupTimerRef,
    popupDelayMs,
    setLogoutFromDemo,
    setShowWelcomeModal,
  };

  return (
    <PatientDemoContext.Provider value={value}>
      {children}
      <DemoModal />
      <ConfirmModal
        isVisible={showWelcomeModal}
        confirmText={'Weiter'}
        contentText={
          'In dieser Demo-Version können Sie ausprobieren, wie das Üben mit der App funktioniert. Wählen Sie zuerst ein Übungsset aus und tippen Sie dann auf „Weiter“. Danach wählen Sie die Aufgaben aus und tippen auf „Start“.'
        }
        confirmAction={() => setShowWelcomeModal((prev) => !prev)}
        headerText={'Willkommen in der neolexon Aphasie-App für Patient:innen!'}
        noBackground
        hasBorder
        minimized
      />
    </PatientDemoContext.Provider>
  );
}

export function usePatientDemo(): PatientDemoContextType {
  const context = useContext(PatientDemoContext);
  if (context === undefined) {
    logError('usePatientDemo must be used within a PatientDemoProvider');
    throw new Error('usePatientDemo must be used within a PatientDemoProvider');
  }
  return context;
}
